import { CommonModule, DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, OnChanges, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { TablerIconsModule } from 'angular-tabler-icons';
import * as constants from 'src/app/common/constant/constants.type';
import { MedicalReportType, MedicalReportTypeLabels } from 'src/app/common/enums/medical-report-type';
import { TranslationUtils } from 'src/app/common/utils/translation.utils';
import { MaterialModule } from 'src/app/material.module';
import { environment } from 'src/environments/environment';
import { MedicalRecordDetailComponent } from './medical-record-detail/medical-record-detail.component';
import { MedicalRecordService } from './medical.service';
import { IMedicalRecord } from './medical.type';
import { HttpHeaders } from '@angular/common/http';

@Component({
  selector: 'app-medical-record',
  imports: [CommonModule, MaterialModule, TablerIconsModule],
  providers: [DatePipe],
  templateUrl: './medical-record.component.html',
  styleUrl: './medical-record.component.scss',
})
export class MedicalRecordComponent implements OnInit, OnDestroy, OnChanges {
  @Input() patientId!: string | number | undefined;
  @Input() type?: string;
  @Output() medicalRecordLength = new EventEmitter<number>();

  medicalRecords: IMedicalRecord[] = [];

  readonly medicalReportType = MedicalReportType;
  readonly medicalReportTypeLabels = MedicalReportTypeLabels;

  private destroy$ = new Subject<void>();

  constructor(
    public dialog: MatDialog,
    private medicalRecordService: MedicalRecordService,
    private datePipe: DatePipe,
    private sanitizer: DomSanitizer,
    private toastr: ToastrService,
    private translationUtils: TranslationUtils,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    // Handled by ngOnChanges
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['patientId'] && changes['patientId'].currentValue) {
      this.loadMedicalRecords();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onDownload(record: IMedicalRecord): void {
    this.medicalRecordService.downloadMedicalRecord(record.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ body, headers }) => {
        if (!body) return;

        const fileName = this.extractFileName(headers, record.fileName);
        this.triggerDownload(body, fileName);
      });
  }

  private extractFileName(headers: HttpHeaders, fallback = 'medical-record'): string {
    const contentDisposition = headers.get('content-disposition') || '';
    const utf8 = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
    const normal = contentDisposition.match(/filename="?([^"]+)"?/i)?.[1];

    return decodeURIComponent(utf8 || normal || fallback);
  }

  private triggerDownload(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = fileName;
    link.click();

    URL.revokeObjectURL(url);
  }

  openPreview(record: IMedicalRecord): void {
    record.fileUrl && window.open(record.fileUrl, '_blank', 'noopener,noreferrer');
  }

  onPreviewLoadError(record: IMedicalRecord): void {
    record.previewType = 'file';
    record.previewUrl = '';
  }

  onDelete(record: IMedicalRecord): void {
    this.medicalRecordService.deleteMedicalRecord(record.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => {
        if (!res.isSuccess) return;

        this.toastr.success(
          this.translationUtils.getTranslatedMessage(
            'COMMON.DELETE',
            this.translate.instant('ENTITY.RECORD')
          )
        );
        this.loadMedicalRecords();
      });
  }

  onUpload(): void {
    this.dialog.open(MedicalRecordDetailComponent, {
      width: '100vw',
      maxWidth: '600px',
      disableClose: true,
      data: { patientId: this.patientId }
    })
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => res && this.loadMedicalRecords());
  }

 loadMedicalRecords(): void {
  this.medicalRecordService.getMedicalRecordByPatientID(Number(this.patientId))
    .pipe(takeUntil(this.destroy$))
    .subscribe({
      next: (res) => {
        if (!this.isValidResponse(res)) {
          this.resetRecords();
          return;
        }

        const filteredRecords = this.type 
          ? this.filterRecordsByType(res.result, this.type)
          : res.result;

        this.updateMedicalRecords(filteredRecords);
      },
      error: (error) => {
        console.error('Failed to load medical records:', error);
        this.resetRecords();
      }
    });
}

private isValidResponse(res: any): boolean {
  return res?.isSuccess && Array.isArray(res.result) && res.result.length > 0;
}

private filterRecordsByType(records: any[], type: string): any[] {
  const normalizedType = type.toLowerCase().trim();
  const reportType = this.getReportTypeEnum(normalizedType);

  return records.filter(record => 
    this.matchesRecordType(record, normalizedType, reportType)
  );
}

private getReportTypeEnum(typeStr: string): MedicalReportType | null {
  const typeMap: Record<string, MedicalReportType> = {
    'ipd': MedicalReportType.IPD,
    'opd': MedicalReportType.OPD,
    'pastdocument': MedicalReportType.PastDocument
  };

  return typeMap[typeStr] ?? null;
}

private matchesRecordType(record: any, typeStr: string, reportType: MedicalReportType | null): boolean {
  if (reportType === null) return false;

  // Normalize record type for comparison
  const recordType = record.type;
  
  return (String(recordType).toLowerCase() === typeStr || recordType === reportType ||Number(recordType) === reportType);
}

private updateMedicalRecords(records: any[]): void {
  this.medicalRecords = records.map(this.mapRecord.bind(this));
  this.medicalRecordLength.emit(this.medicalRecords.length);
}

  private resetRecords(): void {
    this.medicalRecords = [];
    this.medicalRecordLength.emit(0);
  }

  private mapRecord(record: IMedicalRecord): IMedicalRecord {
    const fileName = this.getFileName(record.filePath);
    const fileUrl = this.buildFileUrl(record.filePath);
    const previewType = this.getPreviewType(fileName);

    return {
      ...record,
      description: record.description || 'Medical Record',
      uploadDate: this.datePipe.transform(record.uploadDate, constants.DDMMYYYY_FORMAT) as string,
      fileName,
      fileUrl,
      previewType,
      previewUrl: this.buildPreviewUrl(fileUrl, previewType)
    };
  }

  getRecordTypeLabel(type: MedicalReportType): string {
    return this.medicalReportTypeLabels[type] ?? String(type);
  }

  getFileBadgeLabel(record: IMedicalRecord): string {
    return this.getFileExtension(record.fileName || '')?.toUpperCase() || 'FILE';
  }

  private getFileName(path?: string): string {
    return path?.replace(/\\/g, '/').split('/').pop() || 'medical-record';
  }

  private getFileExtension(fileName: string): string {
    return fileName.split('.').pop()?.toLowerCase() || '';
  }

  private getPreviewType(fileName: string): IMedicalRecord['previewType'] {
    const ext = this.getFileExtension(fileName);

    if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'].includes(ext)) return 'image';
    if (ext === 'pdf') return 'pdf';

    return 'file';
  }

  private buildFileUrl(path?: string): string {
    return path ? `${environment.imagePath}${path.replace(/^\/+/, '')}` : '';
  }

  private buildPreviewUrl(fileUrl: string, type: IMedicalRecord['previewType']) {
    return type === 'pdf'
      ? this.sanitizer.bypassSecurityTrustResourceUrl(fileUrl)
      : fileUrl;
  }
}
